import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, MapPin, Check, Loader2, Sparkles } from "lucide-react";
import { getWaitlistRegions, joinWaitlist } from "../services/api";

/**
 * National Rollout Waitlist capture.
 *
 * Anchors the home-page "national rollout follows" promise as a tangible
 * pipeline: single email field + region dropdown. Lives between the green
 * CTA strip and the About section.
 */
export default function NationalRolloutWaitlist() {
  const [regions, setRegions] = useState([]);
  const [email, setEmail] = useState("");
  const [region, setRegion] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [persona, setPersona] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getWaitlistRegions()
      .then((rs) => {
        if (cancelled) return;
        setRegions(rs);
        // Default to the first non-live region for higher conversion
        const firstNew = rs.find((r) => !r.live);
        setRegion(firstNew?.key || rs[0]?.key || "");
      })
      .catch(() => { /* non-blocking — fall back to free-text */ });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !region || submitting) return;
    setSubmitting(true);
    try {
      const res = await joinWaitlist({
        email,
        region,
        organisation: organisation || undefined,
        persona: persona || undefined,
      });
      setDone(res);
      toast.success(res.message || "You're on the list.");
    } catch (err) {
      toast.error(err.message || "Could not join the waitlist — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-white border-t border-brand-border" data-testid="rollout-waitlist">
      <div className="max-w-[1400px] mx-auto px-6 py-24 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Pitch */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6"
          >
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-brand-accent mb-3 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> National Rollout
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-brand">
              Bring Infralytics to <span className="text-brand-accent">your region next.</span>
            </h2>
            <p className="mt-5 text-[#5C5C59] text-base sm:text-lg leading-relaxed max-w-xl">
              The North East pilot covers 12 Local Authorities and 72,388 postcodes. We&apos;re sequencing the national rollout by demand — councils, operators and investors on the waitlist help us pick what goes live next.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#5C5C59]">
              {[
                "One email when your region is live — no marketing list",
                "Early-access partner pricing for the first 3 councils per region",
                "Optional 30-min discovery call with Nexintell if you'd like to shape priorities",
              ].map((bullet) => (
                <li key={bullet} className="flex gap-2.5 items-start">
                  <Check className="h-4 w-4 text-brand-accent mt-0.5 shrink-0" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Form / success */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6"
          >
            <div className="bg-brand-bg border border-brand-border rounded-xl p-6 sm:p-8">
              {done ? (
                <SuccessCard regionLabel={done.region_label} message={done.message} />
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="waitlist-form">
                  <div>
                    <label htmlFor="wl-email" className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C5C59] mb-1.5 block">
                      Work email
                    </label>
                    <input
                      id="wl-email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="name@your-council.gov.uk"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="waitlist-email"
                      className="w-full bg-white border border-brand-border rounded-md px-4 py-3 text-sm text-brand placeholder:text-[#A8A8A4] focus:outline-none focus:border-brand transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="wl-region" className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C5C59] mb-1.5 block">
                      Notify me when this region goes live
                    </label>
                    <div className="relative">
                      <MapPin className="h-4 w-4 text-[#5C5C59] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <select
                        id="wl-region"
                        required
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        data-testid="waitlist-region"
                        className="w-full appearance-none bg-white border border-brand-border rounded-md pl-10 pr-9 py-3 text-sm text-brand focus:outline-none focus:border-brand transition-colors"
                      >
                        {regions.map((r) => (
                          <option key={r.key} value={r.key}>
                            {r.label}{r.live ? " — Live now" : ""}
                          </option>
                        ))}
                      </select>
                      <ArrowRight className="h-4 w-4 text-[#5C5C59] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none rotate-90" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="wl-org" className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C5C59] mb-1.5 block">
                        Organisation (optional)
                      </label>
                      <input
                        id="wl-org"
                        type="text"
                        placeholder="South Tyneside Council"
                        value={organisation}
                        onChange={(e) => setOrganisation(e.target.value)}
                        data-testid="waitlist-org"
                        className="w-full bg-white border border-brand-border rounded-md px-4 py-3 text-sm text-brand placeholder:text-[#A8A8A4] focus:outline-none focus:border-brand transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="wl-persona" className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C5C59] mb-1.5 block">
                        I am a (optional)
                      </label>
                      <select
                        id="wl-persona"
                        value={persona}
                        onChange={(e) => setPersona(e.target.value)}
                        data-testid="waitlist-persona"
                        className="w-full appearance-none bg-white border border-brand-border rounded-md px-4 py-3 text-sm text-brand focus:outline-none focus:border-brand transition-colors"
                      >
                        <option value="">Select…</option>
                        <option value="council_member">Council member / officer</option>
                        <option value="combined_authority">Combined Authority</option>
                        <option value="dsit">DSIT / Government</option>
                        <option value="operator">Network operator</option>
                        <option value="altnet">AltNet</option>
                        <option value="investor">Investor</option>
                        <option value="researcher">Researcher</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !email || !region}
                    data-testid="waitlist-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white rounded-md px-6 py-3.5 text-sm font-medium hover:bg-brand-deep transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Joining…
                      </>
                    ) : (
                      <>
                        Notify me when it&apos;s live
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-[#5C5C59] leading-relaxed">
                    No spam. One email per region you opt into. Read our{" "}
                    <a href="/privacy" className="text-brand hover:text-brand-accent underline underline-offset-2">privacy policy</a>.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function SuccessCard({ regionLabel, message }) {
  return (
    <div className="text-center py-6" data-testid="waitlist-success">
      <div className="mx-auto h-12 w-12 rounded-full bg-brand-accent/15 flex items-center justify-center mb-4">
        <Check className="h-6 w-6 text-brand-accent" />
      </div>
      <div className="font-heading font-bold text-2xl text-brand mb-2">You&apos;re on the list.</div>
      <p className="text-[#5C5C59] max-w-md mx-auto leading-relaxed">{message}</p>
      <p className="mt-5 text-xs font-mono uppercase tracking-[0.18em] text-brand-accent">
        Region · {regionLabel}
      </p>
    </div>
  );
}
